import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/clientUtils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-75 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    defaultVariants: {
      fullWidth: false,
      size: "default",
      variant: "default",
    },
    variants: {
      fullWidth: {
        false: "w-auto",
        true: "w-full",
      },
      size: {
        default: "h-10 px-3 py-2",
        icon: "h-9 w-9 p-2",
        lg: "h-12 px-6 text-base",
        sm: "h-8 rounded-sm! px-3 text-xs",
        tight: "m-0 gap-0 p-0",
      },
      variant: {
        // Token-based variants
        auth: "inline-flex h-10 w-full cursor-pointer flex-row items-center justify-center rounded-full border border-black/10 px-4 py-2 font-semibold text-sm shadow-sm outline-0 outline-blue-600 outline-offset-2 transition focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-white/10 dark:shadow-none dark:outline-blue-500 forced-colors:outline-[Highlight]",
        "auth-primary":
          "inline-flex h-10 w-full cursor-pointer flex-row items-center justify-center rounded-full border border-black/10 bg-blue-600 px-4 py-2 font-semibold text-sm text-white shadow-sm outline-0 outline-blue-600 outline-offset-2 transition hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-blue-600 focus-visible:outline-offset-2 dark:border-white/10 dark:shadow-none dark:outline-blue-500 forced-colors:outline-[Highlight]",
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive! text-destructive-foreground! hover:bg-destructive/90!",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        icon: "rounded-full hover:bg-gray-200 disabled:opacity-0",
        input:
          "border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        nav: "-ml-1 size-9 h-7 w-7 hover:bg-accent hover:text-accent-foreground",
        outline:
          "border border-neutral-300 bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        text: "hover:underline",
      },
    },
  },
);

interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  tooltip?: React.ReactNode;
}

function Button({
  className,
  variant,
  size,
  fullWidth,
  asChild = false,
  tooltip,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  const button = (
    <Comp
      className={cn(buttonVariants({ className, fullWidth, size, variant }))}
      {...props}
    />
  );

  if (tooltip != null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent className="max-w-64">{tooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return button;
}

export { Button, buttonVariants, type ButtonProps };
